// MonthlyFormazione.jsx - Componente Principale
import React, { useEffect, useState, useContext, useRef } from 'react';
import { Box, Fab, Tooltip, Grid, Typography, useTheme , CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import itLocale from 'date-fns/locale/it';
import { withTranslation } from 'react-i18next';
import TokenContext from 'components/TokenContext';
import { cicassService, auth } from '../../../_services';
import { ToastComponent } from '@syncfusion/ej2-react-notifications';

// Import dei sotto-componenti
import SummaryDialog from './components/SummaryDialogFormazione';
import EventDialog from './components/EventDialog';
import DeleteConfirmDialog from './components/DeleteConfirmDialog';
import CalendarGrid from './components/CalendarGrid';
import { config } from "_helpers";

const daysOfWeek = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const apiUrl = 'formazione';

const MonthlyFormazione = ({ year, month, t , targetEventId, onEventAutoOpened}) => {
  // Stati principali
  const [isLoading, setIsLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < config.mobileSizeCheck);
  
  // Stati per i dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Event management
  const [currentEvent, setCurrentEvent] = useState({
    id: -1,
    descrizione: '',
    tipoLezione: 'SSE',
    start: new Date(),
    durata: 2,
    istruttore1Id: null,
    istruttore2Id: null,
    utentiIstr1: 10,
    utentiIstr2: 5,
    iscritti: []
  });
  const [mode, setMode] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Toast
  const [toastInfo, setToastInfo] = useState({ title: '', content: '', cssClass: '', icon: '' });
  const toastRef = useRef(null);
  
  // Context e auth
  const theme = useTheme();
  const { jwt } = useContext(TokenContext);
  const currentUserId = auth.getUserId(jwt);
  const myRole = auth.getUserRole();
  //const canEdit = ["Admin", "SuperUser", "Manager"].includes(myRole);
  //const isManagerIstruttore = ["Admin", "SuperUser", "Manager"].includes(myRole);
  const isManagerIstruttoreOrAdmin = ["Admin", "SuperUser"].includes(myRole) || 
    (myRole === "Manager" && instructors.some(inst => inst.id === currentUserId));
  const isIstruttoreAssegnato = currentEvent && ( currentEvent.istruttore1Id === currentUserId || currentEvent.istruttore2Id === currentUserId );
  // Effects
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < config.mobileSizeCheck);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          loadEvents(),
          loadInstructors(),
          loadAllUsers()
        ]);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [year, month]);

  
  // Data loading functions
  const loadEvents = async () => {
    try {
      const res = await cicassService.getAll(`${apiUrl}/${year}/${month + 1}`);
      const payload = Array.isArray(res) ? res : res.data;
      
      // Carica gli iscritti per ogni evento
      const eventsWithIscritti = await Promise.all(
        payload.map(async (ev) => {
          try {
            const utentiRes = await cicassService.getAll(`${apiUrl}/users/${ev.id}`);
            const iscrittiData = Array.isArray(utentiRes) ? utentiRes : utentiRes.data || [];
            
            return {
              ...ev,
              start: new Date(ev.start),
              iscritti: iscrittiData
            };
          } catch (error) {
            console.error(`Errore caricamento iscritti per evento ${ev.id}:`, error);
            return {
              ...ev,
              start: new Date(ev.start),
              iscritti: []
            };
          }
        })
      );
 
      setEvents(eventsWithIscritti);
      // 🎯 LOGICA DI APERTURA AUTOMATICA
      if (targetEventId) {
        // Cerchiamo l'evento tra quelli appena scaricati
        const eventToOpen = payload.find(e => String(e.id) === String(targetEventId));
        
        if (eventToOpen) {
          await handleOpenEvent(null,eventToOpen);
          if (onEventAutoOpened) {
            onEventAutoOpened();
          }
        }
      }
    } catch (error) {
      console.error('Errore caricamento eventi:', error);
      showToastInfo('Error', 'Errore nel caricamento degli eventi');
    }
  };

  const loadInstructors = async () => {
    try {
      const res = await cicassService.getAll(`${apiUrl}/istruttori`);
      setInstructors(res.data || []);
    } catch (error) {
      console.error('Errore caricamento istruttori:', error);
    }
  };

  const loadAllUsers = async () => {
    try {
      const res = await cicassService.getAll(`${apiUrl}/userlist`);
      setAllUsers(res.data || []);
    } catch (error) {
      console.error('Errore caricamento utenti:', error);
    }
  };

  // Toast function
  const showToastInfo = (type, message) => {
    let info = { title: '', content: message, cssClass: '', icon: '' };
    switch (type) {
      case 'Success':
        info.title = t('addToastOK');
        info.cssClass = 'e-toast-success';
        info.icon = 'e-success toast-icons';
        break;
      case 'Information':
        info.title = t('editToastOK');
        info.cssClass = 'e-toast-info';
        info.icon = 'e-info toast-icons';
        break;
      case 'Warning':
        info.title = t('deleteToastOK');
        info.cssClass = 'e-toast-warning';
        info.icon = 'e-warning toast-icons';
        break;
      case 'Error':
        info.title = 'Error';
        info.cssClass = 'e-toast-danger';
        info.icon = 'e-error toast-icons';
        break;
      default:
        break;
    }
    setToastInfo(info);
    toastRef.current.show();
  };

  // Event handlers
  const handleOpenEvent = async (date, ev = null) => {
    if (ev) {
      setMode('edit');

      // Se gli iscritti sono già caricati, usali. Altrimenti ricaricali.
      /*if (ev.iscritti && ev.iscritti.length >= 0) {
        setCurrentEvent({
          ...ev,
          iscritti: ev.iscritti, tipoLezione: ev.tipoLezione || 'SSE',   // ⬅ fallback
        });
      } else {*/
        try {
          const utenti = await cicassService.getAll(`${apiUrl}/users/${ev.id}`);
          const iscrittiData = Array.isArray(utenti) ? utenti : utenti.data || [];
          const validStart = ev.start instanceof Date 
            ? ev.start 
            : (ev.start ? new Date(ev.start) : new Date());
          setCurrentEvent({
            ...ev,
            start: validStart,
            iscritti: iscrittiData,
          });
          
          // Aggiorna anche l'evento nella lista
          setEvents(prev => prev.map(event => 
            event.id === ev.id 
              ? { ...event, iscritti: iscrittiData }
              : event
          ));
          
        } catch (error) {
          console.error('Errore caricamento utenti evento:', error);
          setCurrentEvent({ ...ev, iscritti: [] });
        }
      //}
    } else {
      // Se l'utente non ha i permessi di Manager/Admin, non aprire nulla
      if (!isManagerIstruttoreOrAdmin) {
        return;
      }
      const start = new Date(date);
      start.setHours(20, 45);
      setMode('create');
      setCurrentEvent({
        id: -1,
        descrizione: '',
        tipoLezione: 'SSE',
        start,
        durata: 2,
        istruttore1Id: null,
        istruttore2Id: null,
        utentiIstr1: 10,
        utentiIstr2: 5,
        iscritti: [],
      });
    }
    setOpenDialog(true);
  };

  const handleSaveEvent = async (eventData) => {
    try {
      // FIX TIMEZONE: Se start è una stringa, gestisci il timezone
    let fixedStart;
    if (typeof eventData.start === 'string') {
      // Per stringhe datetime, rimuovi la 'Z' o timezone info e aggiungi offset locale
      const localDate = new Date(eventData.start);
      fixedStart = new Date(localDate.getTime() - (localDate.getTimezoneOffset() * 60000));
    } else {
      // Se è già un Date object
      fixedStart = new Date(eventData.start.getTime() - (eventData.start.getTimezoneOffset() * 60000));
    }
    
    const fixedEventData = {
      ...eventData,
      start: fixedStart
    };
      const res = await cicassService.saveOp(apiUrl, fixedEventData);
      const savedEvent = { ...res.data, start: new Date(res.data.start) };
      
      // Carica gli iscritti per il nuovo evento salvato
      try {
        const utentiRes = await cicassService.getAll(`${apiUrl}/users/${savedEvent.id}`);
        const iscrittiData = Array.isArray(utentiRes) ? utentiRes : utentiRes.data || [];
        savedEvent.iscritti = iscrittiData;
      } catch (error) {
        console.error('Errore caricamento iscritti per nuovo evento:', error);
        savedEvent.iscritti = [];
      }
      
      setEvents(prev =>
        currentEvent.id === -1
          ? [...prev, savedEvent]
          : prev.map(ev => ev.id === savedEvent.id ? savedEvent : ev)
      );
      
      setOpenDialog(false);
      await loadEvents();
      showToastInfo('Success', t('Salvataggio avvenuto con successo'));
    } catch (err) {
      console.error('Errore salvataggio formazione:', err);
      showToastInfo('Error', t('Errore salvataggio corso'));
      throw err;
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await cicassService.deleteOp(apiUrl, eventId);
      setEvents(prev => prev.filter(ev => ev.id !== eventId));
      setDeleteDialogOpen(false);
      setOpenDialog(false);
      await loadEvents();
      showToastInfo('Warning', 'Eliminazione avvenuta con successo');
    } catch (err) {
      console.error('Errore eliminazione evento:', err);
      showToastInfo('Error', `Errore: ${err.message}`);
      throw err;
    }
  };

  const handleAddUser = async (userId) => {
    try {
      const res = await cicassService.saveOp(`${apiUrl}/utente`, {
        id: -1,
        formazioneId: currentEvent.id,
        anaUserId: userId
      });

      const user = allUsers.find(u => u.id === userId);
      const newIscritto = {
        id: res.data?.id || Date.now(), // ID temporaneo se non restituito dall'API
        anaUserId: userId,
        name: user?.name || 'N/A',
        createdAt: new Date().toISOString(),
        isPresente: 0 // 0 = non presente inizialmente
      };

      setCurrentEvent(ev => ({
        ...ev,
        iscritti: [...ev.iscritti, newIscritto]
      }));
      
      // Aggiorna anche la lista degli eventi per il refresh del calendario
      setEvents(prev => prev.map(ev => 
        ev.id === currentEvent.id 
          ? { ...ev, iscritti: [...(ev.iscritti || []), newIscritto] }
          : ev
      ));
      
      setSelectedUser(null);
      showToastInfo('Success', t('Utente aggiunto con successo'));
    } catch (err) {
      console.error('Errore aggiunta utente:', err);
      showToastInfo('Error', t('Errore aggiunta utente'));
      throw err;
    }
  };

  const handleDeleteUser = async (idTabella) => {
    try {
      await cicassService.deleteOp(`${apiUrl}/utente`, idTabella);
      
      // Aggiorna currentEvent
      const updatedIscritti = currentEvent.iscritti.filter(u => u.id !== idTabella);
      setCurrentEvent(ev => ({
        ...ev,
        iscritti: updatedIscritti
      }));
      
      // Aggiorna anche la lista degli eventi per il refresh del calendario
      setEvents(prev => prev.map(ev => 
        ev.id === currentEvent.id 
          ? { ...ev, iscritti: updatedIscritti }
          : ev
      ));
      
      showToastInfo("Warning", t("Utente rimosso con successo"));
    } catch (err) {
      console.error('Errore rimozione utente:', err);
      showToastInfo("Error", t("Errore durante la rimozione utente"));
      throw err;
    }
  };

  const handleTogglePresenza = async (userId, currentValue) => {
    try {
      const iscrizione = currentEvent.iscritti.find(u => u.anaUserId === userId);
      if (!iscrizione) {
        showToastInfo('Error', 'Utente non trovato nella lista iscritti');
        return;
      }

      const newValue = currentValue === 1 ? 0 : 1;
      const payload = {
        id: iscrizione.id,
        formazioneId: currentEvent.id,
        anaUserId: userId,
        isPresente: newValue,
        deletedAt: iscrizione.deletedAt || null,
        createdAt: iscrizione.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await cicassService.saveOp(`${apiUrl}/utente/presenza`, payload);

      // Aggiorna currentEvent
      const updatedIscritti = currentEvent.iscritti.map(u =>
        u.anaUserId === userId ? { ...u, isPresente: newValue } : u
      );
      
      setCurrentEvent(ev => ({
        ...ev,
        iscritti: updatedIscritti
      }));
      
      // Aggiorna anche la lista degli eventi per il refresh del calendario  
      setEvents(prev => prev.map(ev => 
        ev.id === currentEvent.id 
          ? { ...ev, iscritti: updatedIscritti }
          : ev
      ));

      showToastInfo('Information', `Presenza ${newValue === 1 ? 'confermata' : 'rimossa'}`);
    } catch (err) {
      console.error('Errore aggiornamento presenza:', err);
      showToastInfo('Error', 'Errore durante aggiornamento presenza');
      throw err;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={itLocale}>
      <Box sx={{ p: 2, position: 'relative' }}>
        {isLoading && (
          <Box sx={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            bgcolor: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            borderRadius: 1
          }}>
            <CircularProgress />
          </Box>
        )}
        {/* FAB Buttons per chi ha i privilegi  */}
        {isManagerIstruttoreOrAdmin && (
          <>
            <Tooltip title={t('Aggiungi Corso')} placement="top">
              <Fab 
                color="primary" 
                sx={{ position: 'fixed', bottom: 80, right: 20, zIndex: 1000 }} 
                onClick={() => handleOpenEvent(new Date(year, month, 1))}
              >
                <AddIcon />
              </Fab>
            </Tooltip>

            <Tooltip title={`Riepilogo Annuale ${year}`} placement="top">
              <Fab 
                color="secondary" 
                sx={{ 
                  position: 'fixed', 
                  bottom: 140, 
                  right: 20, 
                  zIndex: 1000,
                  bgcolor: '#ff9800',
                  color: 'white',
                  '&:hover': { 
                    bgcolor: '#f57c00',
                    transform: 'scale(1.05)'
                  },
                  transition: 'all 0.2s ease'
                }} 
                onClick={() => setSummaryDialogOpen(true)}
              >
                <AssessmentIcon />
              </Fab>
            </Tooltip>
          </>
        )}

        {/* Header giorni della settimana per desktop */}
        {!isMobile && (
          <Grid container>
            {daysOfWeek.map((d, i) => (
              <Grid item xs key={i}>
                <Typography 
                  align="center" 
                  sx={{ 
                    fontWeight: (d === 'Sab' || d === 'Dom') ? 'bold' : 'normal',
                    color: (d === 'Sab' || d === 'Dom') ? '#2c5282' : 'inherit'
                  }}
                >
                  {d}
                </Typography>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Calendario */}
        <CalendarGrid
          year={year}
          month={month}
          events={events}
          instructors={instructors}
          currentUserId={currentUserId}
          isMobile={isMobile}
          theme={theme}
          onEventClick={handleOpenEvent}
        />

        {/* Dialog Riepilogo Annuale */}
        <SummaryDialog
          open={summaryDialogOpen}
          onClose={() => setSummaryDialogOpen(false)}
          year={year}
          apiUrl={apiUrl}
          onToast={showToastInfo}
        />

        {/* Dialog Evento */}
        <EventDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          mode={mode}
          currentEvent={currentEvent}
          setCurrentEvent={setCurrentEvent}
          instructors={instructors}
          allUsers={allUsers}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
          currentUserId={currentUserId}
          isManagerIstruttore={isManagerIstruttoreOrAdmin}
          onSave={handleSaveEvent}
          onDelete={() => setDeleteDialogOpen(true)}
          onAddUser={handleAddUser}
          onDeleteUser={handleDeleteUser}
          onTogglePresenza={handleTogglePresenza}
          onToast={showToastInfo}
          isIstruttore={isIstruttoreAssegnato}
          t={t}
        />

        {/* Dialog Conferma Eliminazione */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={() => handleDeleteEvent(currentEvent.id)}
          t={t}
        />

        {/* Toast Component */}
        <ToastComponent
          ref={toastRef}
          title={toastInfo.title}
          content={toastInfo.content}
          cssClass={toastInfo.cssClass}
          icon={toastInfo.icon}
          position={{ X: 'Right', Y: 'Bottom' }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default withTranslation()(MonthlyFormazione);